import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { DateTime } from 'luxon';
import { UsersService } from '../../@services/users.service';
import { AgreementReviewBadgeService } from '../../@services/agreement-review-badge.service';
import { ToastService } from '../../@services/toast.service';
import { agGridThemes } from '../../@shared/ag-grid-themes';
import { SetFloatingFilter } from '../../@shared/set-floating-filter.component';

/**
 * Exchange Agreements review workflow (IT-F2-391).
 *
 * Standalone panel — spun out of the Compliance Report on Mike's
 * 2026-09-15 c/a4c363b1 ("lets break the compliance review and editor
 * out into its own separate panel instead of in the compliance report").
 *
 * Lists Exchange_Agreements rows that have EVER been through the review
 * workflow (REVIEW_STATUS in pending/approved/declined). Admin can flip
 * to any target state from any current state:
 *   - approve pending          → grants live data
 *   - decline approved         → revokes live data
 *   - approve declined         → revives live data
 *   - approve-as-Pro↔Non-Pro   → re-classify post-approval
 *
 * The Cognito flip (`custom:live_data_access`) fires with the correct
 * target value on every transition. Reason is required-on-decline
 * (SPA-enforced). All decisions land in `F2-ADMIN.ReviewAudit`.
 *
 * Data source: reuses the existing `getExhibitBReport` endpoint (same
 * shape the compliance-report grid uses) and filters client-side to
 * rows that have a REVIEW_STATUS set. No new backend endpoint needed —
 * the payload size is fine for this pass.
 *
 * Live updates (cid b1e2ecd2, 2026-09-15):
 * Subscribes to the `/rest/admin/users-live/stream` SSE bridge — same
 * stream the Users grid consumes — and reacts to `entitlement_changed`
 * envelopes (published by data-agreements.service on any review
 * decision, from any admin). Full refetch on event so the panel
 * matches Refresh-button semantics exactly (belt-and-suspenders vs a
 * per-row patch: safer against schema drift and event coalescing bugs).
 *
 * Bulk actions (cid b1e2ecd2, 2026-09-15):
 * Approve-as-Pro / Approve-as-Non-Pro / Decline moved OUT of per-row
 * cells and UP to a top action bar. Selection via per-row checkboxes
 * driven by a `selection_set: Set<string>` keyed by _row_key. The
 * bulk handlers iterate selected rows sequentially through the same
 * `_do_approve` / `_do_decline` primitives the single-row buttons
 * used to call, so audit trail + Cognito flip semantics are identical
 * to the pre-bulk world. View button stays per-row (unchanged).
 *
 * Admin-role only (see route config).
 */
@Component({
  selector: 'app-exchange-agreement-review',
  imports: [CommonModule, FormsModule, AgGridAngular, RouterLink],
  templateUrl: './exchange-agreement-review.component.html',
})
export class ExchangeAgreementReviewComponent implements OnInit, OnDestroy {
  // ── ag-grid config (IT-F2-413 c/90494e0c — Mike: "switch the grid to
  // have the same features as the admin panel users with filtering
  // column headers etc"). Mirrors the users.component.ts pattern:
  // floatingFilter per column, multiRow selection with checkboxes,
  // agTotalRowCount + agFilteredRowCount status bar. Row IDs match the
  // legacy _row_key(row) so selection_set semantics stay stable.
  @ViewChild(AgGridAngular) agGrid?: AgGridAngular;
  theme = agGridThemes.dark;
  grid: any = {};

  // SetFloatingFilter iterates `params.context.data` — Users grid names
  // that field `data`, but ours is `review_rows`. Getter alias keeps the
  // shared filter component working without refactoring it.
  get data(): any[] { return this.review_rows; }
  // Full report row set (all customers, all statuses). Kept private
  // to this component — the review view only cares about the reviewed
  // subset (filtered below via _rebuild_review_rows). Loaded on init.
  private all_rows: any[] = [];

  // Rows that have been through the review workflow, filtered by the
  // current review_filter pick. Bound to the table in the template.
  review_rows: any[] = [];

  // Per-row draft reason so multiple rows can each have their own text
  // typed before either button is clicked. Keyed by row identifier
  // (USER_ID_INTERNAL||USER_ID + '|' + CUSTOMER).
  review_reason: Record<string, string> = {};

  // In-flight guard per row so double-clicks don't fire the endpoint
  // twice (which would just no-op the second time but still spams the
  // audit log).
  review_busy: Record<string, boolean> = {};

  // Live count of PENDING rows only — surfaces urgency independent of
  // the current filter pick (see sidebar badge if wired later).
  pending_count = 0;

  // Client-side filter on top of the "all-review-statuses" row set.
  // Defaults to 'pending' so the initial view matches the queue of
  // things to decide.
  review_filter: 'all' | 'pending' | 'approved' | 'declined' = 'pending';

  // Free-text search across email / name / customer / user_id — case
  // insensitive substring match. Mike c/9007a7e2 IT-F2-391 2026-09-16:
  // there was no working search input on the panel (the bulk_reason
  // field looked like one but wasn't wired). Applied on top of the
  // review_filter tab pick + rebuilt on every keystroke.
  search_query = '';

  // View Application modal state.
  view_agreement_row: any = null;
  view_agreement_open = false;
  // IT-F2-413 c/be868ff8 (Mike 2026-09-21): visual cue on the modal
  // reason input when the operator clicks Decline while the input is
  // empty. Reduces the friction of the reason-required validation.
  reason_error_flash = false;

  loading = false;

  // ── Bulk selection ─────────────────────────────────────────────────
  // Row keys currently selected via the leading checkbox column. Cleared
  // whenever the filter changes or a bulk action completes. `Set` gives
  // us cheap has/add/delete + a size property for the "N selected" chip.
  selection_set = new Set<string>();

  // Bulk-decline reason shared across all selected rows on Decline. Sits
  // at the top of the action bar; per-row reason input is gone with the
  // per-row buttons. Approve doesn't require a reason so the top input
  // is only wired to the Decline path.
  bulk_reason = '';

  // Bulk-action busy flag — separate from per-row review_busy so the
  // top buttons disable atomically while the sequential per-row loop
  // is in flight, without also disabling the View button on unselected
  // rows.
  bulk_busy = false;

  // ── Live update (SSE) ──────────────────────────────────────────────
  // Subscribes to the users-live SSE stream (same stream Users grid
  // uses). We only care about `op:'entitlement_changed'` envelopes
  // here — every other op (per-user Cognito writes, sync_state,
  // deletes) is ignored. On each entitlement_changed we schedule a
  // debounced full refetch so a burst of decisions from bulk actions
  // fired elsewhere coalesces to a single reload instead of N.
  private _eventSource: EventSource | null = null;
  private _sseReconnectTimer: any = null;
  private _liveRefreshTimer: any = null;
  private static readonly LIVE_REFRESH_DEBOUNCE_MS = 400;
  private static readonly SSE_RECONNECT_MS = 3000;
  // IT-F2-426 c/<xxx.har> (Mike 2026-10-02): HAR showed 3 exhibit-b
  // POSTs in 20s on a quiet session because `entitlement_changed`
  // fires fleet-wide (session displacement + decision writes) and the
  // 400ms debounce isn't a floor. Enforce a minimum interval between
  // load() calls so the heavy report refetch can't fire more than once
  // every 15s regardless of SSE event volume.
  private static readonly MIN_LOAD_INTERVAL_MS = 15_000;
  private _lastLoadMs = 0;

  // Human-readable labels for the 11 questions rendered in the modal.
  // Order matches the source-of-truth exchange-agreements form flow.
  readonly agreement_question_labels: { key: string; label: string }[] = [
    { key: 'personal_use',      label: 'Uses market data for personal, non-business use' },
    { key: 'business_use',      label: 'Uses market data for business use' },
    { key: 'registered_sec',    label: 'Registered with the SEC' },
    { key: 'registered_agency', label: 'Registered with a US state agency (CFTC, NFA, MSRB, state commission)' },
    { key: 'perform_functions', label: 'Performs financial-services functions requiring registration' },
    { key: 'investment_advice', label: 'Provides investment advice for compensation' },
    { key: 'asset_manager',     label: 'Manages assets for others' },
    { key: 'capital_usage',     label: 'Uses market data with capital of others' },
    { key: 'entity_trading',    label: 'Trades on behalf of an entity' },
    { key: 'profit_sharing',    label: 'Shares in profits from market-data-based trading' },
    { key: 'benefits_exchange', label: 'Receives benefits from an exchange for market-data use' },
  ];

  // IT-F2-413 c/f876bfa8 (Mike 2026-09-21): filter state persists in URL
  // so hard-refresh / bookmark restores it. Serialized to a single
  // ?filters=<base64url(json)> param — matches the users-grid share-
  // link shape (users.component.ts:864 _apply_filters_query_param).
  // Debounce writes so a keystroke storm during typing doesn't spam
  // navigate calls.
  private _urlSyncTimer: any = null;
  private static readonly URL_SYNC_DEBOUNCE_MS = 300;

  // IT-F2-426 c/f4081b4b (Mike 2026-10-02): signed-PDF attachment UI
  // state. SPA pre-validates size + MIME before posting to keep the
  // base64 round-trip small.
  signed_pdf_busy = false;
  private static readonly SIGNED_PDF_MAX_BYTES = 10 * 1024 * 1024;

  // IT-F2-426 c/e12385a8: Add Agreement from PDF moved to a dedicated
  // route at /admin/exchange-agreement-review/add-from-pdf so the full
  // standard questionnaire (same shape as /data-agreements) can render
  // on a full page instead of a cramped modal. All the modal + add-
  // from-PDF state used to live here; removed 2026-10-05 after Mike
  // reported seeing a stale version of the old modal in prod — a stale
  // service-worker bundle rendered a shell that could no longer be
  // opened via the swapped toolbar button, so leaving the dead code
  // in-place was keeping the confusion alive.

  // IT-F2-426 c/2795820a (Mike 2026-10-02): Live-updates toggle. On
  // by default; flipping off closes the EventSource so an operator
  // on a slow network / debugging a decision can freeze the view.
  live_updates = true;

  constructor(
    private adminService: UsersService,
    private toast: ToastService,
    private zone: NgZone,
    private route: ActivatedRoute,
    private router: Router,
    private reviewBadge: AgreementReviewBadgeService,
  ) {}

  // Operator flips the Live toggle. Opens or closes the SSE channel
  // and keeps the badge service's own 60s poll independent so the
  // sidenav count never freezes with the review page.
  on_live_toggle_change(enabled: boolean) {
    this.live_updates = !!enabled;
    if (this.live_updates) {
      this._open_event_source();
    } else {
      this._close_event_source();
      if (this._liveRefreshTimer) { clearTimeout(this._liveRefreshTimer); this._liveRefreshTimer = null; }
    }
  }

  async ngOnInit() {
    this.init_grid();
    await this.load();
    this._open_event_source();
  }

  // Grid config init — kept parallel to users.component.ts init_grid()
  // so any operator moving between the two panels sees familiar shapes.
  init_grid() {
    const self = this;
    this.grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        rowSelection: {
          mode: 'multiRow',
          checkboxes: true,
          headerCheckbox: true,
          selectAll: 'filtered',
          enableClickSelection: false, // per-row buttons + row click == select is confusing here; keep to checkbox
        },
        defaultColDef: {
          floatingFilter: true,
          filter: true,
          sortable: true,
          resizable: true,
          cellStyle: { 'text-align': 'left' },
        },
        // Match legacy _row_key: USER_ID_INTERNAL||USER_ID + '|' + CUSTOMER.
        // Keeps selection_set semantics identical across the ag-grid cutover
        // so bulk_approve / bulk_decline don't need touching.
        getRowId: (params: any) => {
          const uid = params.data?.USER_ID_INTERNAL || params.data?.USER_ID || '';
          const customer = params.data?.CUSTOMER || '';
          return `${uid}|${customer}`;
        },
        // Highlight rows in the legacy selection_set on first load so the
        // grid state matches what selection_set already contains after a
        // filter change / live refetch.
        onGridReady: (e: any) => this.on_grid_ready(e),
        // Sync the legacy selection_set to whatever ag-grid holds now.
        // Bulk action code reads selected_rows (derived from selection_set)
        // so keeping selection_set authoritative avoids a wider refactor.
        onSelectionChanged: (e: any) => {
          const selected = e.api.getSelectedRows() as any[];
          self.selection_set.clear();
          for (const r of selected) self.selection_set.add(self._row_key_public(r));
        },
        // IT-F2-413 c/f876bfa8: persist filter + sort state to URL on
        // change so a hard-refresh restores the view. Debounced so live
        // typing on SetFloatingFilter doesn't spam navigate calls.
        onFilterChanged: () => self._schedule_url_sync(),
        onSortChanged: () => self._schedule_url_sync(),
      },
      // agGridReady fires before rowData populates the model; use a
      // statusBar-native row count so admin sees filtered vs total live.
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
          { statusPanel: 'agSelectedRowCountComponent', align: 'left' },
        ],
      },
      columnDefs: [
        // IT-F2-426 Mike 2026-10-09: Actions is the far-left column
        // right after the row-select checkbox (ag-grid auto-renders
        // the checkbox before any user columnDef when rowSelection
        // .checkboxes=true). Operator's primary interaction is Re-eval
        // / View; keeping it at the left means it's always in view
        // regardless of horizontal scroll.
        {
          headerName: 'Actions',
          colId: 'actions',
          minWidth: 110,
          maxWidth: 130,
          filter: false,
          sortable: false,
          cellStyle: { 'text-align': 'left' },
          // IT-F2-413 c/533c3ec8 (Mike 2026-09-22): don't gate the
          // button's `disabled` attribute at cellRenderer time — the
          // renderer captures `review_busy[key]` at render moment,
          // and applyTransaction after a decline can freeze the row
          // with `disabled` set from the earlier busy-true render.
          // Check busy at CLICK time instead so the button re-enables
          // as soon as _do_decline's finally block clears review_busy.
          cellRenderer: (_p: any) => {
            return `
              <button data-action="reeval" class="dark-btn text-xs px-2 py-1 bg-gray-400 text-white mr-1" title="Re-derive Pro status from stored answers"><i class="fa-solid fa-arrows-rotate"></i></button>
              <button data-action="view" class="dark-btn text-xs px-2 py-1 bg-gray-500 text-white" title="View"><i class="fa-solid fa-eye"></i></button>
            `;
          },
          onCellClicked: (params: any) => {
            const target = (params?.event?.target as HTMLElement | null);
            if (!target) return;
            const btn = target.closest?.('button[data-action]') as HTMLButtonElement | null;
            if (!btn) return;
            // Check busy at click time so a stale render doesn't
            // permanently disable the buttons after a decline.
            const key = self._row_key_public(params.data);
            const busy = !!self.review_busy[key] || self.bulk_busy;
            if (busy) return;
            const action = btn.getAttribute('data-action');
            if (action === 'view') self.open_view_application(params.data);
            else if (action === 'reeval') self.on_reeval_row(params.data);
          },
        },
        // IT-F2-413 c/cdbbaaf6 (Mike 2026-09-21): text columns need the
        // SetFloatingFilter component (input + X clear button + type-and-
        // live-filter) to match the Users grid UX. Default ag-grid text
        // floating filter renders a plain input without X — Mike's
        // screenshot flagged the missing X. SetFloatingFilter iterates
        // `context.data` — see the `data` getter above (aliases
        // review_rows).
        {
          field: 'SUBSCRIBERS_EMAIL_ADDRESS',
          headerName: 'Email',
          minWidth: 220,
          floatingFilterComponent: SetFloatingFilter,
          valueGetter: (p: any) =>
            p.data?.SUBSCRIBERS_EMAIL_ADDRESS || p.data?.USER_ID_INTERNAL || p.data?.USER_ID || '',
          cellStyle: { 'font-family': 'monospace' },
        },
        {
          field: 'SUBSCRIBERS_FIRST_NAME',
          headerName: 'First Name',
          minWidth: 120,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: 'SUBSCRIBERS_LAST_NAME',
          headerName: 'Last Name',
          minWidth: 120,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: 'CUSTOMER',
          headerName: 'Customer',
          minWidth: 110,
          maxWidth: 160,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          headerName: 'Submitted',
          colId: 'submitted',
          minWidth: 150,
          filter: 'agDateColumnFilter',
          valueGetter: (p: any) => p.data?.DATE_AND_TIME_STAMP || p.data?.EARLIEST_DATE || null,
          valueFormatter: (p: any) => self._format_date(p.value),
        },
        {
          headerName: 'Last Edited',
          colId: 'edited',
          minWidth: 150,
          filter: 'agDateColumnFilter',
          valueGetter: (p: any) => p.data?.EDITED_AT || null,
          valueFormatter: (p: any) => self._format_date(p.value),
        },
        {
          field: 'PRO_STATUS',
          headerName: 'Application',
          minWidth: 100,
          maxWidth: 130,
          floatingFilterComponent: SetFloatingFilter,
          cellRenderer: (p: any) => {
            const v = p.value;
            if (v === 'Pro') return '<span class="text-blue-600 dark:text-blue-400">Pro</span>';
            if (v === 'Non-Pro') return '<span class="text-green-600 dark:text-green-400">Non-Pro</span>';
            return '<span class="text-gray-500">—</span>';
          },
        },
        {
          field: 'REVIEW_STATUS',
          headerName: 'Status',
          minWidth: 150,
          floatingFilterComponent: SetFloatingFilter,
          cellRenderer: (p: any) => {
            const chip = self.status_chip_for(p.data);
            return `<span class="inline-block px-2 py-0.5 rounded text-[11px] font-medium ${chip.classes}">${chip.label}</span>`;
          },
        },
        // IT-F2-426 c/f4081b4b (Mike 2026-10-02): at-a-glance paperclip
        // for rows with a signed PDF attached. Click opens the inline
        // PDF viewer (same URL the modal's View button uses).
        {
          field: 'HAS_SIGNED_PDF',
          headerName: 'PDF',
          minWidth: 80,
          maxWidth: 90,
          sortable: true,
          filter: 'agSetColumnFilter',
          floatingFilterComponent: SetFloatingFilter,
          cellStyle: { 'text-align': 'center' },
          cellRenderer: (p: any) => {
            if (!p?.data?.HAS_SIGNED_PDF) return '<span class="text-gray-400">—</span>';
            const title = p.data.SIGNED_PDF?.filename
              ? `${p.data.SIGNED_PDF.filename}${p.data.SIGNED_PDF.size ? ' · ' + self.format_pdf_size(p.data.SIGNED_PDF.size) : ''}`
              : 'Signed PDF attached';
            return `<i class="fa-regular fa-file-pdf text-red-500" title="${title.replace(/"/g, '&quot;')}"></i>`;
          },
          onCellClicked: (evt: any) => {
            if (!evt?.data?.HAS_SIGNED_PDF) return;
            const url = self.signed_pdf_url(evt.data, false);
            if (url) window.open(url, '_blank', 'noopener');
          },
        },
      ],
    };
  }

  on_grid_ready(_e: any) {
    // IT-F2-413 c/f876bfa8: restore ?filters=<b64> from URL if present,
    // so hard-refresh keeps the filter + sort state Mike had before.
    this._restore_filters_from_url();
  }

  // ── URL filter sync (IT-F2-413 c/f876bfa8) ────────────────────────
  // Snapshot ag-grid's filterModel + sort state → base64url(JSON) into
  // the `filters` query param. Mirrors the users-grid share-link shape.
  // Also carries the top-toolbar review_filter tab pick + search_query
  // so a hard-refresh restores the ENTIRE filter view, not just the
  // per-column filters.
  private _schedule_url_sync(): void {
    if (this._urlSyncTimer) clearTimeout(this._urlSyncTimer);
    this._urlSyncTimer = setTimeout(() => {
      this._urlSyncTimer = null;
      this._sync_filters_to_url();
    }, ExchangeAgreementReviewComponent.URL_SYNC_DEBOUNCE_MS);
  }

  private _sync_filters_to_url(): void {
    if (!this.agGrid?.api) return;
    const model: any = {};
    try { model.filter = this.agGrid.api.getFilterModel() || {}; } catch { model.filter = {}; }
    try {
      const cs = this.agGrid.api.getColumnState?.() || [];
      const sortState = cs
        .filter((c: any) => c.sort)
        .map((c: any) => ({ colId: c.colId, sort: c.sort, sortIndex: c.sortIndex }));
      if (sortState.length) model.sort = sortState;
    } catch { /* ignore */ }
    // Also persist the top-toolbar controls so hard-refresh restores
    // the whole view state, not just the per-column filters.
    if (this.review_filter !== 'pending') model.tab = this.review_filter;
    if ((this.search_query || '').trim()) model.q = this.search_query;
    let param: string | null = null;
    // Empty state → clear the URL param entirely so the URL stays clean.
    const has_state =
      (model.filter && Object.keys(model.filter).length > 0) ||
      (Array.isArray(model.sort) && model.sort.length > 0) ||
      !!model.tab || !!model.q;
    if (has_state) {
      try {
        const json = JSON.stringify(model);
        param = btoa(json).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      } catch { /* ignore */ }
    }
    // replaceUrl so back-button doesn't accumulate a history entry per
    // keystroke; queryParamsHandling: 'merge' preserves any unrelated
    // params (none today, but future-proof).
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { filters: param },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    }).catch(() => { /* best-effort */ });
  }

  private _restore_filters_from_url(): void {
    if (!this.agGrid?.api) return;
    const raw = this.route.snapshot.queryParamMap.get('filters');
    if (!raw) return;
    let json: string;
    try {
      const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
      const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
      json = atob(b64 + pad);
    } catch { return; }
    let model: any;
    try { model = JSON.parse(json); } catch { return; }
    if (!model || typeof model !== 'object') return;
    // Restore per-column ag-grid state
    if (model.filter && typeof model.filter === 'object') {
      try { this.agGrid.api.setFilterModel(model.filter); } catch { /* ignore */ }
    }
    if (Array.isArray(model.sort) && model.sort.length) {
      try { this.agGrid.api.applyColumnState({ state: model.sort, defaultState: { sort: null } }); } catch { /* ignore */ }
    }
    // Restore the top-toolbar tab pick + search box.
    if (model.tab === 'pending' || model.tab === 'approved' || model.tab === 'declined' || model.tab === 'all') {
      if (model.tab !== this.review_filter) {
        this.review_filter = model.tab;
        this._rebuild_review_rows();
      }
    }
    if (typeof model.q === 'string' && model.q) {
      this.search_query = model.q;
      this._rebuild_review_rows();
    }
  }

  // ag-grid CSV export — matches the users-grid pattern (filtered + sorted
  // rows only). Fires from the top toolbar button.
  export_csv() {
    if (!this.agGrid?.api) return;
    const stamp = DateTime.now().toFormat('yyyyLLdd-HHmm');
    try {
      this.agGrid.api.exportDataAsCsv({
        fileName: `agreement-review-${this.review_filter}-${stamp}.csv`,
        exportedRows: 'filteredAndSorted',
      });
      this.toast.success('CSV export', 'Downloaded the currently-filtered rows.');
    } catch (e: any) {
      this.toast.error('CSV export', String(e?.message || e));
    }
  }

  // Public wrapper for the private _row_key so ag-grid callbacks (which
  // can't easily reach private methods via arrow-function context) can
  // still produce the compound key.
  _row_key_public(row: any): string {
    return this._row_key(row);
  }

  private _format_date(v: any): string {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '—';
    return DateTime.fromJSDate(d).toFormat('yyyy-LL-dd HH:mm ZZZZ');
  }

  ngOnDestroy(): void {
    this._close_event_source();
    if (this._liveRefreshTimer) {
      clearTimeout(this._liveRefreshTimer);
      this._liveRefreshTimer = null;
    }
  }

  async load() {
    this.loading = true;
    try {
      // No customer/date-range filters here — the review queue is
      // fleet-wide by default. If it grows large we can add a filter
      // strip; today's payload is well under the perf threshold.
      const res: any = await this.adminService.getExhibitBReport({});
      this.all_rows = (res && !res.err && Array.isArray(res.rows)) ? res.rows : [];
      this._rebuild_review_rows();
      // IT-F2-426 2026-10-02: stamp AFTER a successful completion so
      // the SSE floor only counts fully-fetched loads (an in-flight
      // load's duplicate SSE event still coalesces correctly via the
      // debounce timer).
      this._lastLoadMs = Date.now();
    }
    finally {
      this.loading = false;
    }
  }

  // Filter this.all_rows by the current review_filter pick.
  //
  // - 'all'      → EVERY completed agreement (Mike 2026-09-16 chat: filter
  //                labelled 'all' should mean all, not just workflow-scoped;
  //                the review-scoped subset is still reachable via
  //                pending/approved/declined tabs). Pre-workflow legacy rows
  //                with review_status=null are included and render as
  //                'unreviewed' in the STATUS column.
  // - 'pending'  → REVIEW_STATUS === 'pending' only
  // - 'approved' → REVIEW_STATUS === 'approved' only
  // - 'declined' → REVIEW_STATUS === 'declined' only
  //
  // Badge count is PENDING-only regardless of the current filter —
  // admins care about urgency of the queue, not size of display.
  //
  // Also prunes selection_set entries for rows that fell out of the
  // current filter (or vanished entirely on refetch) so the "N selected"
  // count doesn't include stale keys.
  private _rebuild_review_rows() {
    const source = Array.isArray(this.all_rows) ? this.all_rows : [];
    const f = this.review_filter;
    let scoped: any[];
    if (f === 'all') {
      scoped = source;
    } else {
      scoped = source.filter((r: any) =>
        String(r?.REVIEW_STATUS || '').toLowerCase() === f
      );
    }
    const q = (this.search_query || '').trim().toLowerCase();
    if (q) {
      scoped = scoped.filter((r: any) => {
        const email = String(r?.SUBSCRIBERS_EMAIL_ADDRESS || '').toLowerCase();
        const first = String(r?.SUBSCRIBERS_FIRST_NAME || '').toLowerCase();
        const last  = String(r?.SUBSCRIBERS_LAST_NAME || '').toLowerCase();
        const name  = `${first} ${last}`.trim();
        const cust  = String(r?.CUSTOMER || '').toLowerCase();
        const uid_i = String(r?.USER_ID_INTERNAL || '').toLowerCase();
        const uid   = String(r?.USER_ID || '').toLowerCase();
        return email.includes(q) || name.includes(q) || first.includes(q) ||
               last.includes(q)  || cust.includes(q) || uid_i.includes(q) ||
               uid.includes(q);
      });
    }
    // IT-F2-413 c/419b86b2 (Mike 2026-09-21): always .slice() for a
    // fresh array reference so ag-grid's [rowData] binding re-diffs
    // even when the filter didn't add/remove rows. Without this, an
    // in-place row mutation (REVIEW_STATUS flip in _do_decline) leaves
    // the grid rendering stale cells because rebuild produced the same
    // Array reference.
    this.review_rows = scoped.slice();
    this.pending_count = source.filter((r: any) =>
      String(r?.REVIEW_STATUS || '').toLowerCase() === 'pending'
    ).length;
    this._prune_selection();
    // Explicit push into ag-grid — the input binding SHOULD pick this
    // up but Angular's OnPush + ag-grid immutable-mode diffing can miss
    // in-place cell changes when getRowId returns the same value. Belt-
    // and-suspenders so the grid re-renders reliably after a decision.
    if (this.agGrid?.api) {
      try { this.agGrid.api.setGridOption('rowData', this.review_rows); } catch { /* ignore */ }
    }
  }

  on_search_change(value: string) {
    this.search_query = String(value || '');
    // Search change re-scopes selection just like filter change — drop
    // any keys that fell out of view so bulk actions can't hit invisible
    // rows.
    this.selection_set.clear();
    this._rebuild_review_rows();
    this._schedule_url_sync();
  }

  private _prune_selection() {
    if (this.selection_set.size === 0) return;
    const visible = new Set<string>(this.review_rows.map((r: any) => this._row_key(r)));
    const drop: string[] = [];
    for (const k of this.selection_set) {
      if (!visible.has(k)) drop.push(k);
    }
    for (const k of drop) this.selection_set.delete(k);
  }

  on_review_filter_change(value: 'all' | 'pending' | 'approved' | 'declined') {
    this.review_filter = value;
    // Filter change re-scopes what "select all" means; clear selection
    // so the operator doesn't accidentally bulk-act on rows they can't
    // see. Cheaper cognitive load than trying to preserve cross-filter
    // selection.
    this.selection_set.clear();
    this._rebuild_review_rows();
    this._schedule_url_sync();
  }

  // ── Selection helpers ─────────────────────────────────────────────
  is_selected(row: any): boolean {
    return this.selection_set.has(this._row_key(row));
  }

  toggle_selected(row: any, checked: boolean): void {
    const key = this._row_key(row);
    if (checked) this.selection_set.add(key);
    else this.selection_set.delete(key);
  }

  is_all_visible_selected(): boolean {
    if (this.review_rows.length === 0) return false;
    for (const r of this.review_rows) {
      if (!this.selection_set.has(this._row_key(r))) return false;
    }
    return true;
  }

  // Partial-selection indeterminate state — some but not all visible
  // rows selected. Bound to the header checkbox's `indeterminate` DOM
  // property via a template ref.
  is_some_visible_selected(): boolean {
    if (this.review_rows.length === 0) return false;
    let seen = 0;
    for (const r of this.review_rows) {
      if (this.selection_set.has(this._row_key(r))) seen++;
    }
    return seen > 0 && seen < this.review_rows.length;
  }

  toggle_all_visible(checked: boolean): void {
    if (checked) {
      for (const r of this.review_rows) this.selection_set.add(this._row_key(r));
    } else {
      for (const r of this.review_rows) this.selection_set.delete(this._row_key(r));
    }
  }

  clear_selection(): void {
    this.selection_set.clear();
  }

  // Rows currently selected AND still present in the filtered view —
  // the bulk operations iterate this list. Returned as an array so
  // template-side `*ngFor` can render preview UI if we want it later.
  get selected_rows(): any[] {
    if (this.selection_set.size === 0) return [];
    return this.review_rows.filter((r: any) => this.selection_set.has(this._row_key(r)));
  }

  // ── Per-row action disable helpers (kept so View + row-level
  // status chips can still reflect state; bulk-button disable logic
  // lives in the bulk_*_disabled getters below).
  is_approve_pro_disabled(row: any): boolean {
    if (this.is_review_busy(row)) return true;
    const status = String(row?.REVIEW_STATUS || '').toLowerCase();
    const is_pro = row?.PRO_STATUS === 'Pro';
    return status === 'approved' && is_pro;
  }
  is_approve_non_pro_disabled(row: any): boolean {
    if (this.is_review_busy(row)) return true;
    const status = String(row?.REVIEW_STATUS || '').toLowerCase();
    const is_non_pro = row?.PRO_STATUS === 'Non-Pro';
    return status === 'approved' && is_non_pro;
  }
  is_decline_disabled(row: any): boolean {
    if (this.is_review_busy(row)) return true;
    const status = String(row?.REVIEW_STATUS || '').toLowerCase();
    return status === 'declined';
  }

  // Stable key for per-row indexing — matches USER_ID_INTERNAL||USER_ID
  // + '|' + CUSTOMER (same shape the compliance-report uses).
  private _row_key(row: any): string {
    const uid = row?.USER_ID_INTERNAL || row?.USER_ID || '';
    const customer = row?.CUSTOMER || this._resolve_customer_slug(row) || '';
    return `${uid}|${customer}`;
  }

  private _resolve_customer_slug(row: any): string {
    if (row?.CUSTOMER) return row.CUSTOMER;
    return '';
  }

  // ── Bulk action button disable/label helpers ────────────────────────
  // Effective-changes count: rows where clicking the target button
  // would actually flip state (not already in target state). Used for
  // the "Will affect N users" preview so admins can see the delta
  // BEFORE clicking. Buttons still fire on all selected rows — the
  // per-row `_do_approve` / `_do_decline` primitives no-op when the
  // row is already in the target state (endpoint checks server-side
  // too), so this is a UX-preview count only.
  effective_approve_pro_count(): number {
    let n = 0;
    for (const r of this.selected_rows) {
      if (!this.is_approve_pro_disabled(r)) n++;
    }
    return n;
  }
  effective_approve_non_pro_count(): number {
    let n = 0;
    for (const r of this.selected_rows) {
      if (!this.is_approve_non_pro_disabled(r)) n++;
    }
    return n;
  }
  effective_decline_count(): number {
    let n = 0;
    for (const r of this.selected_rows) {
      if (!this.is_decline_disabled(r)) n++;
    }
    return n;
  }

  bulk_approve_pro_disabled(): boolean {
    if (this.bulk_busy) return true;
    return this.selection_set.size === 0 || this.effective_approve_pro_count() === 0;
  }
  bulk_approve_non_pro_disabled(): boolean {
    if (this.bulk_busy) return true;
    return this.selection_set.size === 0 || this.effective_approve_non_pro_count() === 0;
  }
  bulk_decline_disabled(): boolean {
    if (this.bulk_busy) return true;
    if (this.selection_set.size === 0) return true;
    if (this.effective_decline_count() === 0) return true;
    // Reason required for decline path — bulk shares one reason across
    // all selected rows so the button greys until the operator types
    // one in the top input.
    if (!this.bulk_reason.trim()) return true;
    return false;
  }

  // ── Per-row action entry points (still used by the View modal
  // footer, which keeps single-row buttons — the panel table itself
  // no longer renders per-row action buttons post cid b1e2ecd2). ────
  async on_approve_review_as_pro(row: any) {
    return this._do_approve(row, true);
  }
  async on_approve_review_as_non_pro(row: any) {
    return this._do_approve(row, false);
  }

  private async _do_approve(row: any, pro_override: boolean | undefined) {
    const key = this._row_key(row);
    if (this.review_busy[key]) return;
    const user_id = row?.USER_ID_INTERNAL || row?.USER_ID;
    const customer_slug = this._resolve_customer_slug(row);
    if (!user_id || !customer_slug) {
      this.toast.error('Cannot approve', 'missing user_id or customer on row');
      return;
    }
    // Prefer per-row reason if the operator typed one (via the View
    // modal); fall back to the bulk_reason input at the top of the
    // panel so a single reason applies to a whole bulk approve if
    // the operator wants to leave one.
    const reason = (this.review_reason[key] || this.bulk_reason || '').trim();
    const prior_status = String(row?.REVIEW_STATUS || '').toLowerCase();
    this.review_busy[key] = true;
    try {
      const res: any = await this.adminService.reviewUserDataAgreement(
        user_id, customer_slug, 'approved', reason || undefined, pro_override,
      );
      if (res?.err || !res?.success) {
        this.toast.error('Approve failed', res?.err || 'server did not confirm');
        return;
      }
      let toast_body: string;
      if (pro_override === true) {
        toast_body = 'Approved as Pro — user remains in delayed data (per spec).';
        row.PRO_STATUS = 'Pro';
      }
      else if (pro_override === false) {
        toast_body = 'Approved as Non-Pro — live data granted.';
        row.PRO_STATUS = 'Non-Pro';
      }
      else {
        const is_pro = row?.PRO_STATUS === 'Pro';
        toast_body = is_pro
          ? 'Pro user — remains in delayed data (per spec).'
          : 'Non-Pro — live data granted.';
      }
      const toast_title = prior_status === 'declined' ? 'Revived' : 'Approved';
      this.toast.success(toast_title, toast_body);
      row.REVIEW_STATUS = 'approved';
      row.REVIEW_REVIEWED_AT = new Date().toISOString();
      this._rebuild_review_rows();
      this._apply_row_update(row);
      delete this.review_reason[key];
      if (this.view_agreement_row === row) {
        this.close_view_application();
      }
      // IT-F2-426 c/2795820a: nudge sidenav badge so it drops promptly.
      this.reviewBadge.bump();
    }
    finally {
      this.review_busy[key] = false;
    }
  }

  async on_decline_review(row: any) {
    return this._do_decline(row);
  }

  // IT-F2-391 2026-09-15 (Mike c/ff3ca265 — Re-eval button): force a
  // pro_access + live_data_access re-derivation on the row from its
  // currently-stored answers. Shares the review_busy map with the
  // approve/decline handlers so a concurrent double-click during a
  // pending Approve doesn't fire two writes. Toast copy compares
  // before → after so the admin knows whether the re-eval actually
  // changed anything.
  async on_reeval_row(row: any) {
    return this._do_reeval(row);
  }

  private async _do_reeval(row: any) {
    const key = this._row_key(row);
    if (this.review_busy[key]) return;
    const user_id = row?.USER_ID_INTERNAL || row?.USER_ID;
    const customer_slug = this._resolve_customer_slug(row);
    if (!user_id || !customer_slug) {
      this.toast.error('Cannot re-eval', 'missing user_id or customer on row');
      return;
    }
    this.review_busy[key] = true;
    try {
      const res: any = await this.adminService.reevaluateUserDataAgreement(user_id, customer_slug);
      if (res?.err || !res?.success) {
        this.toast.error('Re-eval failed', res?.err || 'server did not confirm');
        return;
      }
      const before = res.before || {};
      const after = res.after || {};
      const pro_changed = before.pro_access !== after.pro_access;
      const live_changed = before.live_data_access !== after.live_data_access;
      let toast_title = 'Re-evaluated';
      let toast_body: string;
      if (!pro_changed && !live_changed) {
        toast_body = after.pro_access
          ? 'No change — still Pro (delayed data).'
          : 'No change — still Non-Pro (live data).';
      }
      else {
        const parts: string[] = [];
        if (pro_changed) {
          const from_pro = before.pro_access ? 'Pro' : 'Non-Pro';
          const to_pro = after.pro_access ? 'Pro' : 'Non-Pro';
          parts.push(`${from_pro} → ${to_pro}`);
        }
        if (live_changed) {
          parts.push(after.live_data_access ? 'live data granted' : 'live data revoked');
        }
        toast_body = parts.join('. ') + '.';
      }
      this.toast.success(toast_title, toast_body);
      // Update the row inline so the UI reflects the new classification
      // without waiting for the SSE-driven refetch (which will also fire
      // via the entitlement_changed publish and coalesce via
      // _liveRefreshTimer).
      row.PRO_STATUS = after.pro_access ? 'Pro' : 'Non-Pro';
      row.EDITED_AT = new Date().toISOString();
      this._rebuild_review_rows();
      this._apply_row_update(row);
    }
    finally {
      this.review_busy[key] = false;
    }
  }

  private async _do_decline(row: any) {
    const key = this._row_key(row);
    if (this.review_busy[key]) return;
    const user_id = row?.USER_ID_INTERNAL || row?.USER_ID;
    const customer_slug = this._resolve_customer_slug(row);
    if (!user_id || !customer_slug) {
      this.toast.error('Cannot decline', 'missing user_id or customer on row');
      return;
    }
    // Reason resolution order: per-row (View modal) > bulk_reason (top
    // action-bar input). Bulk-decline path relies on bulk_reason; the
    // single-row decline from the View modal footer keeps using the
    // per-row reason input inside the modal.
    const reason = (this.review_reason[key] || this.bulk_reason || '').trim();
    if (!reason) {
      // IT-F2-413 c/43928d7a (Mike 2026-09-21): don't fire toast.error
      // for form-validation issues — that mints an ErrorReport and
      // pollutes /admin/error-reports with UX-shaped noise. Use warning
      // toast (no ER submission) and rely on the red-flash + autofocus
      // pulse to show WHERE the missing input is.
      this.toast.warning('Reason required', 'Enter a reason before declining a submission.');
      // Flash the modal reason input red + move focus back to it so the
      // operator sees WHERE to type. Cleared automatically when they
      // start typing.
      this.reason_error_flash = true;
      // Best-effort focus: if the modal is open, the input has a stable
      // marker via [autofocus]; querySelector re-focuses it. Silent skip
      // if not in a browser context (SSR / test).
      if (typeof document !== 'undefined') {
        setTimeout(() => {
          const el = document.querySelector<HTMLInputElement>(
            'input[placeholder="Optional for approve, required for decline"]'
          );
          try { el?.focus(); } catch { /* ignore */ }
        }, 0);
      }
      return;
    }
    const prior_status = String(row?.REVIEW_STATUS || '').toLowerCase();
    this.review_busy[key] = true;
    try {
      const res: any = await this.adminService.reviewUserDataAgreement(
        user_id, customer_slug, 'declined', reason,
      );
      if (res?.err || !res?.success) {
        this.toast.error('Decline failed', res?.err || 'server did not confirm');
        return;
      }
      if (prior_status === 'approved') {
        this.toast.success('Revoked', 'Revoked live data access — user reverted to delayed mode.');
      }
      else {
        this.toast.success('Declined', 'User stays in delayed data mode.');
      }
      row.REVIEW_STATUS = 'declined';
      row.REVIEW_REASON = reason;
      row.REVIEW_REVIEWED_AT = new Date().toISOString();
      this._rebuild_review_rows();
      this._apply_row_update(row);
      delete this.review_reason[key];
      if (this.view_agreement_row === row) {
        this.close_view_application();
      }
      // IT-F2-426 c/2795820a: nudge sidenav badge.
      this.reviewBadge.bump();
    }
    finally {
      this.review_busy[key] = false;
    }
  }

  // IT-F2-413 c/428b29ad (Mike 2026-09-22): after in-place row mutation
  // in _do_approve / _do_decline / _do_reeval, tell ag-grid to re-render
  // THIS row's cells. Ag-grid v33+ immutable-data mode diffs by object
  // identity — mutating fields on the SAME row object leaves the diff
  // seeing "no change" and the chip stays stale until a full refetch.
  // applyTransaction({update:[row]}) forces the cell re-render regardless.
  // If the row's new REVIEW_STATUS makes it fall OUT of the current
  // review_filter, _rebuild_review_rows already dropped it from
  // review_rows + we push a fresh rowData — this handler is redundant
  // in that case but harmless.
  private _apply_row_update(row: any): void {
    if (!this.agGrid?.api || !row) return;
    try { this.agGrid.api.applyTransaction({ update: [row] }); } catch { /* ignore */ }
    // Also targeted refreshCells on the row's node so the status / pro
    // chip cell renderers re-run. redraws only the changed cells, not
    // the whole row.
    try {
      const rowKey = this._row_key(row);
      const node = this.agGrid.api.getRowNode(rowKey);
      if (node) this.agGrid.api.refreshCells({ rowNodes: [node], force: true });
    } catch { /* ignore */ }
  }

  // ── Bulk action handlers ────────────────────────────────────────────
  // Sequential per-row awaits so per-row busy flags render one at a
  // time (progress indicator per row) and the audit log lands in a
  // predictable order. The endpoint is idempotent so parallel would
  // be safe too, but the visible progress signal is worth the wall-
  // clock cost on typical selection sizes (<20 rows).
  async bulk_approve_as_pro(): Promise<void> {
    if (this.bulk_approve_pro_disabled()) return;
    await this._run_bulk((row) => this._do_approve(row, true), 'Approve as Pro');
  }
  async bulk_approve_as_non_pro(): Promise<void> {
    if (this.bulk_approve_non_pro_disabled()) return;
    await this._run_bulk((row) => this._do_approve(row, false), 'Approve as Non-Pro');
  }
  async bulk_decline(): Promise<void> {
    if (this.bulk_decline_disabled()) return;
    await this._run_bulk((row) => this._do_decline(row), 'Decline');
  }

  // IT-F2-391 2026-09-15 (Mike c/ff3ca265 — Re-eval button): bulk
  // re-eval iterates selected rows sequentially through _do_reeval —
  // same audit trail + Cognito flip semantics as the single-row path.
  // No "would-change" preview count because we can't know whether a
  // row's classification would flip without hitting the endpoint;
  // per-row toasts inside _do_reeval carry that signal.
  async bulk_reeval(): Promise<void> {
    if (this.bulk_reeval_disabled()) return;
    await this._run_bulk((row) => this._do_reeval(row), 'Re-eval');
  }

  bulk_reeval_disabled(): boolean {
    if (this.bulk_busy) return true;
    return this.selection_set.size === 0;
  }

  // Wrap sequential per-row action calls so bulk_busy + selection
  // reset + a summary toast are handled uniformly across all 3 bulk
  // buttons. Snapshots the selection before iterating because
  // _do_approve/_do_decline mutate review_rows via _rebuild_review_rows,
  // which prunes selection_set out from under us mid-loop.
  private async _run_bulk(action: (row: any) => Promise<void>, label: string): Promise<void> {
    const targets = this.selected_rows.slice();
    if (targets.length === 0) return;
    this.bulk_busy = true;
    let ok = 0;
    let failed = 0;
    try {
      for (const row of targets) {
        try {
          await action(row);
          ok++;
        } catch (e: any) {
          failed++;
          console.warn(`bulk ${label} row failed:`, e?.message || e);
        }
      }
      if (failed > 0) {
        this.toast.error(`${label}: partial`, `${ok} succeeded, ${failed} failed`);
      } else {
        // Individual per-row toasts already fired inside _do_approve /
        // _do_decline; the summary toast here is only for the failure
        // path. Success path stays quiet to avoid toast spam on 20-row
        // bulks.
      }
      // Clear the shared reason after a successful bulk decline so it
      // doesn't accidentally carry over to a follow-up bulk approve.
      if (failed === 0) this.bulk_reason = '';
      this.selection_set.clear();
      // IT-F2-426 c/2795820a: bulk ops already fire per-row bumps via
      // _do_approve / _do_decline, but trigger one more at the end
      // after selection_set.clear() so the badge reflects the final
      // post-bulk count even if one row's bump landed before another's
      // DB write.
      this.reviewBadge.bump();
    } finally {
      this.bulk_busy = false;
    }
  }

  open_view_application(row: any) {
    this.view_agreement_row = row;
    this.view_agreement_open = true;
    this.reason_error_flash = false;
    // Default to the current (latest) submission on open. History nav
    // walks previous_submissions[] backwards from there.
    this.submission_history_index = 0;
    // IT-F2-400 c/eb18c8dd (Mike 2026-09-16): lazy-fetch prior submissions
    // ONLY when this row has any (TOTAL_SUBMISSION_COUNT > 1). Avoids a
    // wasted round-trip for single-attempt users (the common case) and
    // avoids preloading history for rows the admin never opens.
    this.loaded_prior_submissions = [];
    const total = Number(row?.TOTAL_SUBMISSION_COUNT || 0);
    if (total > 1) {
      const uid = row?.USER_ID_INTERNAL || row?.USER_ID || '';
      const cust = row?.CUSTOMER || '';
      this.history_loading = true;
      this.adminService.getAgreementHistory(String(uid), String(cust))
        .then((res: any) => {
          if (!this.view_agreement_open || this.view_agreement_row !== row) return;
          this.loaded_prior_submissions = Array.isArray(res?.previous_submissions)
            ? res.previous_submissions
            : [];
        })
        .finally(() => { this.history_loading = false; });
    }
  }
  close_view_application() {
    this.view_agreement_open = false;
    this.view_agreement_row = null;
    this.submission_history_index = 0;
    this.loaded_prior_submissions = [];
    this.history_loading = false;
  }

  // ── Submission history navigation ─────────────────────────────────
  // Index 0 = current submission (view_agreement_row itself); 1..N =
  // prior submissions from loaded_prior_submissions[], with 1 =
  // most-recent prior and N = oldest kept snapshot. Backend caps the
  // array at 20 most-recent. TOTAL_SUBMISSION_COUNT is authoritative
  // for the "Attempt X of Y" display even when the array is capped.
  //
  // Mike c/97b0c021 IT-F2-391 2026-09-16 (initial) →
  // Mike c/eb18c8dd IT-F2-400 2026-09-16 (lazy-fetch refactor).
  submission_history_index = 0;
  loaded_prior_submissions: any[] = [];
  history_loading = false;

  private _prior_submissions_for_row(_row: any): any[] {
    // Post-eb18c8dd: history no longer inline on the row. Uses the
    // lazily-fetched array populated by open_view_application().
    return this.loaded_prior_submissions;
  }

  private _current_submission_snapshot_for_row(row: any): any {
    // The "current" submission is a snapshot of the user-supplied fields
    // on the row itself. Kept in the same shape as PREVIOUS_SUBMISSIONS
    // entries so the modal renderer treats index 0 identically.
    if (!row) return null;
    const detail = row.APPLICATION_DETAIL || {};
    return {
      first_name: row.SUBSCRIBERS_FIRST_NAME || '',
      last_name: row.SUBSCRIBERS_LAST_NAME || '',
      email: row.SUBSCRIBERS_EMAIL_ADDRESS || '',
      phone: row.SUBSCRIBERS_TELEPHONE_NUMBER || '',
      outside_us: detail.outside_us || '',
      address: detail.address || '',
      city: detail.city || '',
      state: detail.state || '',
      zip_code: detail.zip_code || '',
      country: detail.country || '',
      employment_status: detail.employment_status || '',
      occupation: row.SUBSCRIBERS_OCCUPATION || '',
      job_title: row['SUBSCRIBERS_TITLE/POSITION'] || '',
      employment_function: row.SUBSCRIBERS_EMPLOYMENT_FUNCTION || '',
      other_employment: detail.other_employment || '',
      employer: {
        name: row.EMPLOYERS_NAME || '',
        address: row.EMPLOYERS_ADDRESS || '',
        city: row.EMPLOYERS_CITY || '',
        state: row.EMPLOYERS_STATE || '',
        zip_code: row.EMPLOYERS_ZIP_CODE || '',
        country: row.EMPLOYERS_COUNTRY || '',
      },
      employer_outside_us: detail.employer_outside_us || '',
      questions: detail.questions || {},
      pro_access: row.PRO_STATUS === 'Pro',
      review_status: row.REVIEW_STATUS || '',
      review_reason: row.REVIEW_REASON || '',
      review_actor_email: row.REVIEW_REVIEWER_EMAIL || '',
      review_action_at: row.REVIEW_REVIEWED_AT || null,
      submitted_at: row.EDITED_AT || row.DATE_AND_TIME_STAMP || null,
    };
  }

  // Snapshot the modal is currently displaying — index 0 = current row,
  // 1..N = prior submissions (most-recent-first).
  displayed_submission(): any {
    if (!this.view_agreement_row) return null;
    if (this.submission_history_index === 0) {
      return this._current_submission_snapshot_for_row(this.view_agreement_row);
    }
    const prior = this._prior_submissions_for_row(this.view_agreement_row);
    // prior[] is stored oldest→newest by mongo $push semantics; walking
    // backwards from length-1 gives most-recent-prior first.
    const arr_idx = prior.length - this.submission_history_index;
    return arr_idx >= 0 ? prior[arr_idx] : null;
  }

  has_prior_submissions(): boolean {
    // Prefer TOTAL_SUBMISSION_COUNT (authoritative, on the row) so the
    // nav strip renders immediately without waiting for the lazy history
    // fetch. Falls back to the loaded array's length for legacy rows
    // with no counter.
    const row = this.view_agreement_row;
    if (!row) return false;
    const total = Number(row.TOTAL_SUBMISSION_COUNT || 0);
    if (total > 1) return true;
    return this._prior_submissions_for_row(row).length > 0;
  }

  total_submission_count(): number {
    const row = this.view_agreement_row;
    if (!row) return 0;
    const explicit = row.TOTAL_SUBMISSION_COUNT;
    if (typeof explicit === 'number' && explicit > 0) return explicit;
    // Legacy fallback for pre-counter rows.
    return this._prior_submissions_for_row(row).length + 1;
  }

  can_go_prev_submission(): boolean {
    // Older direction — increase the index (higher = further back).
    // Disabled while the history is still lazy-loading so we don't move
    // the index past the yet-unpopulated array.
    if (this.history_loading) return false;
    const total_prior = this._prior_submissions_for_row(this.view_agreement_row).length;
    return this.submission_history_index < total_prior;
  }

  can_go_next_submission(): boolean {
    // Newer direction — decrease the index toward 0 (current).
    return this.submission_history_index > 0;
  }

  go_prev_submission() {
    if (this.can_go_prev_submission()) this.submission_history_index++;
  }

  go_next_submission() {
    if (this.can_go_next_submission()) this.submission_history_index--;
  }

  // "Attempt X of Y" label for the modal header.
  submission_attempt_label(): string {
    const total = this.total_submission_count();
    if (total <= 1) return '';
    // Index 0 = latest (attempt N); index k = attempt N-k.
    const attempt = Math.max(1, total - this.submission_history_index);
    return `Attempt ${attempt} of ${total}`;
  }

  // Copy the currently-viewed agreement row as pretty-printed JSON
  // to the clipboard. Guards navigator.clipboard for older/insecure-
  // context browsers with a hidden-textarea + execCommand fallback.
  async copy_agreement_json() {
    if (!this.view_agreement_row) return;
    let json = '';
    try {
      json = JSON.stringify(this.view_agreement_row, null, 2);
    }
    catch (e: any) {
      this.toast.error('Copy failed', `Could not serialize agreement (${e?.message || 'unknown error'})`);
      return;
    }
    const has_clipboard = typeof navigator !== 'undefined'
      && !!navigator.clipboard
      && typeof navigator.clipboard.writeText === 'function';
    if (has_clipboard) {
      try {
        await navigator.clipboard.writeText(json);
        this.toast.success('Copied', 'Agreement JSON copied to clipboard');
        return;
      }
      catch {
        // fall through to hidden-textarea fallback
      }
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = json;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) {
        this.toast.success('Copied', 'Agreement JSON copied to clipboard');
        return;
      }
    }
    catch {
      // fall through to error toast
    }
    this.toast.error('Copy failed', 'Clipboard permission denied?');
  }

  // Yes/no coercion for the modal — mongo doc has yes/no strings (or
  // undefined for un-answered questions on incomplete rows).
  render_yes_no(v: any): string {
    const s = String(v || '').trim().toLowerCase();
    if (s === 'yes') return 'Yes';
    if (s === 'no') return 'No';
    return '—';
  }

  // IT-F2-426 c/f4081b4b (Mike 2026-10-02): signed-PDF modal helpers.
  format_pdf_size(bytes: any): string {
    const n = Number(bytes || 0);
    if (!n) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  }

  signed_pdf_url(row: any, download: boolean): string {
    const user_id = String(row?.F2_USER_ID || row?.USER_ID || '');
    const customer = String(row?.CUSTOMER || '');
    if (!user_id || !customer) return '';
    return this.adminService.signedPdfUrl(user_id, customer, download);
  }

  pick_signed_pdf(_row: any, input: HTMLInputElement) {
    if (!input) return;
    input.value = '';   // allow re-selecting the same file
    input.click();
  }

  async on_signed_pdf_selected(event: Event, row: any) {
    const input = event.target as HTMLInputElement;
    const file = input?.files && input.files[0];
    if (!file) return;
    if (file.type && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      this.toast.error('Only PDFs allowed', `Selected file "${file.name}" is not a PDF.`);
      return;
    }
    if (file.size > ExchangeAgreementReviewComponent.SIGNED_PDF_MAX_BYTES) {
      this.toast.error('File too large', `Signed PDFs must be ${Math.floor(ExchangeAgreementReviewComponent.SIGNED_PDF_MAX_BYTES / (1024 * 1024))} MB or smaller.`);
      return;
    }
    const user_id = String(row?.F2_USER_ID || row?.USER_ID || '');
    const customer = String(row?.CUSTOMER || '');
    if (!user_id || !customer) {
      this.toast.error('Missing identifiers', 'Row has no USER_ID + CUSTOMER — cannot attach PDF.');
      return;
    }
    this.signed_pdf_busy = true;
    try {
      // IT-F2-426 c/<chunked-upload> (Mike 2026-10-05): fan the PDF
      // across sub-500 KB chunks so the shared nginx 1 MB body cap on
      // web-backend-srvr-1 doesn't drop it mid-transit (Vercel reflects
      // the cut as ROUTER_EXTERNAL_TARGET_CONNECTION_ERROR_CD8 / 502).
      const upload_id = await this._chunk_upload_pdf(file);
      if (!upload_id) return;
      const res: any = await this.adminService.finalizeAttachSignedPdfUpload({
        upload_id,
        user_id,
        customer,
        filename: file.name,
        content_type: 'application/pdf',
      });
      if (res && !res.err && res.signed_pdf) {
        // Mutate the open modal row + the grid row so the UI reflects
        // the new attachment without a full reload.
        row.SIGNED_PDF = res.signed_pdf;
        row.HAS_SIGNED_PDF = true;
      }
    } finally {
      this.signed_pdf_busy = false;
    }
  }

  // Upload `file` in chunks and return the server-side upload_id, or
  // '' on failure (with user-visible toast already surfaced). Caller
  // then calls one of the pdf-upload/{parse,create,attach} finalizers
  // with that id. Server buffer is cleaned up on error.
  private async _chunk_upload_pdf(file: File): Promise<string> {
    const began = await this.adminService.beginPdfUpload();
    if (!began?.upload_id) {
      this.toast.error('Upload failed', began?.err || 'could not begin upload');
      return '';
    }
    const upload_id = began.upload_id;
    const chunk_bytes = Math.max(64 * 1024, Math.min(began.chunk_bytes || 384 * 1024, 512 * 1024));
    const bytes = new Uint8Array(await file.arrayBuffer());
    let index = 0;
    for (let offset = 0; offset < bytes.byteLength; offset += chunk_bytes) {
      const slice = bytes.subarray(offset, Math.min(offset + chunk_bytes, bytes.byteLength));
      const b64 = this._bytes_to_base64(slice);
      const ack: any = await this.adminService.appendPdfChunk(upload_id, index, b64);
      if (ack?.err) {
        this.toast.error('Upload failed', ack.err);
        void this.adminService.abortPdfUpload(upload_id);
        return '';
      }
      index++;
    }
    return upload_id;
  }

  private _bytes_to_base64(bytes: Uint8Array): string {
    let binary = '';
    const window_sz = 0x8000;
    for (let i = 0; i < bytes.length; i += window_sz) {
      const sub = bytes.subarray(i, Math.min(i + window_sz, bytes.length));
      binary += String.fromCharCode.apply(null, Array.from(sub));
    }
    return btoa(binary);
  }

  async remove_signed_pdf(row: any) {
    const user_id = String(row?.F2_USER_ID || row?.USER_ID || '');
    const customer = String(row?.CUSTOMER || '');
    if (!user_id || !customer) return;
    if (typeof window !== 'undefined' && !window.confirm('Remove the signed PDF from this agreement?')) return;
    this.signed_pdf_busy = true;
    try {
      const res: any = await this.adminService.removeSignedPdf(user_id, customer);
      if (res && !res.err) {
        row.SIGNED_PDF = null;
        row.HAS_SIGNED_PDF = false;
      }
    } finally {
      this.signed_pdf_busy = false;
    }
  }

  // Compute the Status column value.
  status_chip_for(row: any): { label: string; classes: string } {
    const status = String(row?.REVIEW_STATUS || 'pending').toLowerCase();
    if (status === 'approved') {
      const pro = row?.PRO_STATUS === 'Pro';
      return pro
        ? { label: 'Pro — Approved',     classes: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' }
        : { label: 'Non-Pro — Approved', classes: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' };
    }
    if (status === 'declined') {
      return { label: 'Declined', classes: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' };
    }
    return { label: 'Pending Review', classes: 'bg-gray-100 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300' };
  }

  // Bind helpers for the per-row reason input (Angular can't ngModel
  // into an indexed access expression directly without a getter/setter pair).
  get_review_reason(row: any): string {
    return this.review_reason[this._row_key(row)] || '';
  }
  set_review_reason(row: any, value: string): void {
    this.review_reason[this._row_key(row)] = value;
    // Typing clears the red-flash cue — no need to keep scolding once
    // the operator started fixing it.
    if (value && value.trim()) this.reason_error_flash = false;
  }
  is_review_busy(row: any): boolean {
    return !!this.review_busy[this._row_key(row)];
  }

  // ── SSE live-update wiring ─────────────────────────────────────────
  // Mirrors the Users grid + Error Reports EventSource pattern: mint a
  // short-lived HMAC bearer via POST /users-live/stream-token, open the
  // SSE stream with the token on the query string (cross-origin cookie
  // delivery is unreliable), reconnect on error with a re-minted token.
  //
  // We only act on `entitlement_changed` envelopes — the users-live
  // stream carries every F2:USER_UPDATES publish (per-user Cognito
  // writes, sync_state, deletes) and we don't need any of them here.
  // On event: debounced full refetch so a bulk-flip storm from another
  // admin coalesces to one reload.
  private async _open_event_source() {
    if (!this.live_updates) return;   // IT-F2-426 c/2795820a: respect toggle
    if (this._eventSource) return;
    if (this._sseReconnectTimer) return;
    const token = await this.adminService.mintUsersStreamToken();
    if (!token) {
      this._schedule_sse_reconnect();
      return;
    }
    try {
      const url = this.adminService.getUsersStreamUrl(token);
      const es = new EventSource(url);
      this._eventSource = es;
      es.onmessage = (ev: MessageEvent) => {
        if (!ev?.data) return;
        try {
          const evt = JSON.parse(ev.data);
          // IT-F2-426 c/800822df (Mike 2026-10-02): push-driven updates
          // via dedicated `review_delta` events. `kind:decision` patches
          // the matching row in place (no refetch). `kind:submitted`
          // schedules a floored refetch so new pending rows show up —
          // happens rarely, 15s floor keeps it cheap. Old
          // `entitlement_changed` events are now a NO-OP on the review
          // page (they still fire fleet-wide for Cognito-flip
          // tracking, but refetching the whole exhibit-b on every one
          // was the HAR-captured hard-refresh storm).
          if (evt?.op === 'review_delta') {
            this.zone.run(() => this._apply_review_delta(evt));
          }
        } catch { /* malformed event; ignore */ }
      };
      es.onerror = () => {
        // Token TTL is 60s; close + re-mint rather than looping with
        // an expired one.
        this._close_event_source();
        this._schedule_sse_reconnect();
      };
    } catch (e: any) {
      console.warn('exchange-agreement-review stream open failed:', e?.message);
      this._eventSource = null;
      this._schedule_sse_reconnect();
    }
  }

  private _schedule_sse_reconnect() {
    if (this._sseReconnectTimer) return;
    this._sseReconnectTimer = setTimeout(() => {
      this._sseReconnectTimer = null;
      this._open_event_source();
    }, ExchangeAgreementReviewComponent.SSE_RECONNECT_MS);
  }

  private _close_event_source() {
    if (this._eventSource) {
      try { this._eventSource.close(); } catch { /* ignore */ }
      this._eventSource = null;
    }
    if (this._sseReconnectTimer) {
      clearTimeout(this._sseReconnectTimer);
      this._sseReconnectTimer = null;
    }
  }

  // Coalesce N entitlement_changed events arriving in rapid succession
  // (bulk-flip from another admin) to a single getExhibitBReport call.
  // 400ms window is well under a human's response time to the change
  // so it still feels live, but wide enough to eat a 20-row bulk-approve
  // storm as one refresh.
  // IT-F2-426 c/800822df (Mike 2026-10-02): apply a server-pushed
  // review_delta to the local row set. `kind:decision` finds the row
  // by (user_id, customer) and patches REVIEW_STATUS / PRO_STATUS /
  // REVIEW_REVIEWED_AT / REVIEW_REASON in place. `kind:submitted`
  // falls through to the floored refetch so new pending rows surface.
  // Row not found on a decision also falls back to the refetch — safer
  // than silently dropping an event if the SPA loaded a stale snapshot.
  private _apply_review_delta(evt: any) {
    try {
      if (!evt) return;
      const kind = String(evt?.kind || '').toLowerCase();
      if (kind === 'submitted') {
        this._schedule_live_refresh();
        this.reviewBadge.bump();
        return;
      }
      if (kind !== 'decision') return;
      const uid = String(evt?.user_id || '').toLowerCase();
      const cust = String(evt?.customer || '').trim();
      if (!uid || !cust) return;
      const match = (r: any) => {
        const r_uid = String(r?.USER_ID_INTERNAL || r?.USER_ID || r?.F2_USER_ID || '').toLowerCase();
        const r_cust = String(r?.CUSTOMER || '').trim();
        return r_uid === uid && r_cust === cust;
      };
      const row = (Array.isArray(this.all_rows) ? this.all_rows : []).find(match);
      if (!row) {
        // Row wasn't in the current snapshot — probably a stale load.
        // Pull the next window via the floored refresh instead of
        // dropping the event. Still badge-bump so the sidenav count
        // isn't stuck.
        this._schedule_live_refresh();
        this.reviewBadge.bump();
        return;
      }
      const new_status = String(evt?.review_status || '').toLowerCase();
      if (new_status === 'approved' || new_status === 'declined' || new_status === 'pending') {
        row.REVIEW_STATUS = new_status;
      }
      if (typeof evt?.pro_access === 'boolean') {
        row.PRO_STATUS = evt.pro_access ? 'Pro' : 'Non-Pro';
      }
      if (evt?.reviewed_at) row.REVIEW_REVIEWED_AT = evt.reviewed_at;
      if (typeof evt?.reason === 'string' || evt?.reason === null) row.REVIEW_REASON = evt.reason;
      if (typeof evt?.reviewer_email === 'string') row.REVIEW_REVIEWED_BY = evt.reviewer_email;
      this._rebuild_review_rows();
      this._apply_row_update(row);
      this.reviewBadge.bump();
    } catch (e: any) {
      console.warn('_apply_review_delta failed, falling back to refetch:', e?.message || e);
      this._schedule_live_refresh();
    }
  }

  private _schedule_live_refresh() {
    if (this._liveRefreshTimer) clearTimeout(this._liveRefreshTimer);
    // Honor MIN_LOAD_INTERVAL_MS floor: if we loaded less than 15s ago,
    // push this refresh out so it coincides with the floor boundary
    // (so a storm of SSE events in a quiet window becomes one load,
    // not N). Still runs after the full debounce if we're past the
    // floor already.
    const sinceLast = Date.now() - this._lastLoadMs;
    const delay = Math.max(
      ExchangeAgreementReviewComponent.LIVE_REFRESH_DEBOUNCE_MS,
      ExchangeAgreementReviewComponent.MIN_LOAD_INTERVAL_MS - sinceLast,
    );
    this._liveRefreshTimer = setTimeout(() => {
      this._liveRefreshTimer = null;
      this.load().catch(() => { /* best-effort; next event will retry */ });
    }, delay);
  }
}
